import {
  Button,
  DatePicker,
  Empty,
  Key,
  message,
  Modal,
  MXTabsProps,
  Radio,
  Tabs,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import {
  apiV2AlarmRecordAnalysisCountAlarmPost,
  apiV2AlarmRecordAnalysisGetSceneTabListPost,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import generalActive from './imgs/generalActive.png';
import generalPassive from './imgs/generalPassive.png';
import { Link, useSearchParams } from 'react-router-dom';
import { DateType, DateTypeDisplay } from './const';
import { RadioChangeEvent } from 'antd/lib/radio';
import { isNil } from 'lodash-es';
import EachAlarmDetails from './components/EachAlarmDetails';
import AlarmRecordModal from './components/AlarmRecordModal';
import { getDefaultTime } from './utils';
import qs from 'qs';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/utils';

const RangePicker = DatePicker.RangePicker;

type RangeValue = [Dayjs | null, Dayjs | null] | null;

export const AlarmRecordsAnalysisContext = createContext<{
  selectedSceneId?: number;
  value?: RangeValue;
  timeResolution?: DateType;
  activeKey?: string;
  setAlarmModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setSearchParams?: React.Dispatch<React.SetStateAction<SearchParams | undefined>>;
  searchParams?: SearchParams;
  sceneTabList?: {
    id?: number;
    name?: string;
  }[];
}>({});

export interface SearchParams {
  sceneId?: number;
  startTime?: number;
  endTime?: number;
  timeResolution?: string;
  state?: string;
  levels?: number[];
  deviceName?: string;
  ruleLabelIds?: number[];
  areaLabelId?: number;
  deviceLabelIds?: number[];
}

const AlarmRecordsAnalysis = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [selectedSceneId, setSelectedSceneId] = useState<number>(); // 选择场景
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.DAY); // 选择时间颗粒度
  const [value, setValue] = useState<RangeValue>();
  const [activeKey, setActiveKey] = useState<string>('all');
  const [alarmModalOpen, setAlarmModalOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useState<SearchParams>(); // 弹窗参数
  const [listNumber, setListNumber] = useState<number>();
  const hasConfigPermission = useHasPermission(PermissionsType.B_ALARMRECORDSTATISTICALANALYSIS);

  const [urlSearchParams] = useSearchParams();
  const resolution = urlSearchParams.get('resolution') ?? undefined;

  useEffect(() => {
    resolution && setTimeResolution(resolution as DateType);
  }, [resolution]);

  // 筛选条件
  useEffect(() => {
    setSearchParams({
      sceneId: selectedSceneId,
      timeResolution: timeResolution === DateType.CUSTOM ? 'D' : timeResolution,
      state: activeKey === 'all' ? undefined : activeKey,
      startTime: getDefaultTime(timeResolution, value?.[0]).startTime,
      endTime: getDefaultTime(timeResolution, value?.[1]).endTime,
    });
  }, [activeKey, selectedSceneId, value, timeResolution]);

  useEffect(() => {
    if (timeResolution) {
      switch (timeResolution) {
        case DateType.DAY:
          setValue?.([dayjs().subtract(30, 'day'), dayjs()]);
          break;
        case DateType.WEEK:
          setValue([dayjs().subtract(4, 'week'), dayjs()]);
          break;
        case DateType.MONTH:
          setValue([dayjs().subtract(11, 'month'), dayjs()]);
          break;
        case DateType.CUSTOM:
          setValue?.([dayjs().subtract(30, 'day').startOf('minute'), dayjs().startOf('minute')]);
          break;
      }
    }
  }, [timeResolution]);

  // 获取租户配置的场景TAB列表
  const { data: sceneTabList } = useRequest(async () => {
    const res = await apiV2AlarmRecordAnalysisGetSceneTabListPost({});
    return res.list;
  });

  // 统计报警数量
  const { data: alarmCount } = useRequest(
    async () => {
      return await apiV2AlarmRecordAnalysisCountAlarmPost({
        sceneId: selectedSceneId,
        state: activeKey === 'all' ? undefined : activeKey,
        timeResolution: timeResolution === DateType.CUSTOM ? 'D' : timeResolution,
        startTime: getDefaultTime(timeResolution, value?.[0]).startTime,
        endTime: getDefaultTime(timeResolution, value?.[1]).endTime,
      });
    },
    {
      ready: !!timeResolution && !!value && !!activeKey,
      refreshDeps: [timeResolution, selectedSceneId, value, activeKey],
    }
  );

  // tabs format
  const sceneTabListTabs = useMemo(() => {
    return (sceneTabList ?? []).map(item => ({
      name: item.name,
      id: item.id,
    }));
  }, [sceneTabList]);

  const disabledDate = (current: Dayjs) => {
    // if (timeResolution === DateType.DAY) {
    //   return current && current > dayjs().startOf('day');
    // } else {
    //   let tooLate = null;
    //   let tooEarly = null;
    //   const afterToday = current && current > dayjs().endOf('day');
    //   if (DateType.MONTH === timeResolution) {
    //     tooLate = value?.[0] && current.diff(value?.[0], 'months') >= 23;
    //     tooEarly = value?.[1] && value?.[1].diff(current, 'months') >= 23;
    //   }
    //   return !!tooEarly || !!tooLate || !!afterToday;
    // }
    return current && current > dayjs();
  };

  const onChangePicker = (dates: RangeValue) => {
    let isFlag = true;
    switch (timeResolution) {
      case DateType.DAY:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(31, 'day').valueOf() < dates?.[1].valueOf()) {
          isFlag = false;
          message.error('最多选择31天');
        }
        break;
      case DateType.WEEK:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(5, 'week').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择5周');
          isFlag = false;
        }
        break;
      case DateType.MONTH:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(12, 'month').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择12个月');
          isFlag = false;
        }
        break;
      case DateType.CUSTOM:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(30, 'day').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择31天');
          isFlag = false;
        }
        break;
    }

    if (isFlag) {
      setValue(dates);
    }
  };

  // render picker
  const picker = useMemo(() => {
    if (timeResolution === DateType.MONTH) {
      return 'month';
    } else if (timeResolution === DateType.WEEK) {
      return 'week';
    } else if (timeResolution === DateType.CUSTOM) {
      return undefined;
    } else {
      return 'date';
    }
  }, [timeResolution]);

  const items = useMemo(() => {
    return [
      {
        key: 'all',
        count: alarmCount?.totalCount,
        label: <>全部（{!isNil(alarmCount?.totalCount) ? alarmCount?.totalCount : '-'}）</>,
      },
      {
        key: '0',
        count: alarmCount?.resolvedCount,
        label: <>已恢复（{!isNil(alarmCount?.resolvedCount) ? alarmCount?.resolvedCount : '-'}）</>,
      },
      {
        key: '1',
        count: alarmCount?.unresolvedCount,
        label: <>未恢复（{!isNil(alarmCount?.unresolvedCount) ? alarmCount?.unresolvedCount : '-'}）</>,
      },
    ];
  }, [alarmCount]);

  const exportAction = () => {
    window.open(`/api/v2/rule/alarm/log/exportAlarm` + qs.stringify({ ...searchParams }, { addQueryPrefix: true }));
  };

  return (
    <Wrapper routes={breadcrumbRoutes?.routes}>
      <div className={styles.wrapper}>
        <AlarmRecordsAnalysisContext.Provider
          value={{
            selectedSceneId,
            value,
            timeResolution,
            activeKey,
            setAlarmModalOpen,
            setSearchParams,
            searchParams,
            sceneTabList,
          }}
        >
          <div className={styles.top_wrapper}>
            <Space size="small" wrap>
              <Button
                style={{ paddingLeft: 12, paddingRight: 16 }}
                onClick={() => {
                  // 全部
                  setSelectedSceneId(undefined);
                }}
                className={!selectedSceneId ? styles.titleButtonChecked : styles.titleButtonUnChecked}
              >
                <Space size="small" align="center">
                  <img width={24} src={!selectedSceneId ? generalActive : generalPassive} alt="" />
                  <span>全部</span>
                </Space>
              </Button>
              {sceneTabListTabs.map(sceneItem => (
                <Button
                  key={sceneItem.id}
                  style={{ paddingLeft: 12, paddingRight: 16 }}
                  onClick={() => {
                    setSelectedSceneId(sceneItem.id);
                  }}
                  className={selectedSceneId === sceneItem.id ? styles.titleButtonChecked : styles.titleButtonUnChecked}
                >
                  <Space size="small" align="center">
                    <span>{sceneItem.name}</span>
                  </Space>
                </Button>
              ))}
            </Space>

            <div className={styles.time_box}>
              {/* 日期选择器 */}
              <Space size={16}>
                <Radio.Group
                  onChange={(e: RadioChangeEvent) => {
                    setTimeResolution(e.target.value);
                  }}
                  value={timeResolution}
                  buttonStyle="solid"
                >
                  <Radio.Button value={DateType.DAY}>{DateTypeDisplay[DateType.DAY]}</Radio.Button>
                  <Radio.Button value={DateType.WEEK}>{DateTypeDisplay[DateType.WEEK]}</Radio.Button>
                  <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
                  <Radio.Button value={DateType.CUSTOM}>{DateTypeDisplay[DateType.CUSTOM]}</Radio.Button>
                </Radio.Group>
                <div>
                  <span className={styles.time_label}>时间：</span>
                  <RangePicker
                    picker={picker}
                    showTime={timeResolution === DateType.CUSTOM ? { format: 'HH:mm' } : false}
                    format={timeResolution === DateType.CUSTOM ? 'YYYY-MM-DD HH:mm' : undefined}
                    value={value}
                    disabledDate={disabledDate}
                    onChange={dates => onChangePicker(dates)}
                    allowClear={false}
                  />
                </div>
                <Space size={8}>
                  <Button type="primary" onClick={() => window.open('/device/alarm/record', '_blank')}>
                    详情
                  </Button>
                  {hasConfigPermission && (
                    <Button type="primary" onClick={() => window.open('/alarm/record/analysis/config', '_blank')}>
                      配置
                    </Button>
                  )}
                </Space>
              </Space>
            </div>
          </div>
          <div className={styles.tabs_wrapper}>
            <Tabs
              size="large"
              className={styles.tabs}
              onChange={v => setActiveKey(v)}
              activeKey={activeKey}
              items={items}
            />
            {activeKey && (
              <div className={styles.tab_content}>
                <EachAlarmDetails />
              </div>
            )}
          </div>
          <Modal
            title={
              <div className={styles.title}>
                <div>报警记录</div>
                <div style={{ marginRight: 24 }}>
                  <Button type="primary" disabled={listNumber === 0} onClick={() => exportAction()}>
                    导出
                  </Button>
                </div>
              </div>
            }
            size="big"
            destroyOnClose
            footer={null}
            onCancel={() => {
              setAlarmModalOpen(false);
              setSearchParams({
                sceneId: selectedSceneId,
                timeResolution: timeResolution === DateType.CUSTOM ? 'D' : timeResolution,
                state: activeKey === 'all' ? undefined : activeKey,
                startTime: getDefaultTime(timeResolution, value?.[0]).startTime,
                endTime: getDefaultTime(timeResolution, value?.[1]).endTime,
              });
            }}
            open={alarmModalOpen}
          >
            <AlarmRecordModal alarmModalOpen={alarmModalOpen} setListNumber={setListNumber} />
          </Modal>
        </AlarmRecordsAnalysisContext.Provider>
      </div>
    </Wrapper>
  );
};

export default AlarmRecordsAnalysis;
